import { createStore, combineReducers, applyMiddleware } from "redux";
import thunk from "redux-thunk";
import { composeWithDevTools } from "redux-devtools-extension";
import {
  authLoginReducer,
  authGoogleLoginReducer,
  authResetPasswordReducer,
  authRequestResetPasswordReducer,
  authSendResetEmailAccountReducer,
  authVerifyResetPasswordTokenReducer,
  authUpdatePasswordReducer,
  authVerifyLoginCodeReducer,
  authSelectAccountLoginReducer,
  authSwitchTenantReducer,
} from "./reducers/authReducers";

import {
  tenantDetailsReducer,
  tenantUpdateClinicNameReducer,
  tenantUpdateSupportEmailReducer,
  tenantUpdateOperatingCountryReducer,
  tenantUpdateOnboardingStatusReducer,
  tenantSeedDatabaseUpdateOnboardingStatusReducer,
  tenantUpdateLogoReducer,
  tenantUpdateClinicDetailsReducer,
  tenantUpdateRegionalSettingsReducer,
  tenantUpdateSchedulingSettingsReducer,
  tenantUpdateDentalChairsReducer,
  tenantUpdateCompanyDetailsReducer,
  tenantUpdateSubscriptionStatusReducer,
  tenantGetSubscriptionUsageReducer,
  tenantGetStripeInvoicesReducer,
  tenantGetPublicDetailsReducer,
  tenantDetailsBySlugReducer,
  tenantInviteUserToTenantReducer,
  tenantUpdateDefaultCoordinatorReducer,
  tenantUpdateTenantUserPermissionsReducer,
  tenantDeleteAccountReducer,
} from "./reducers/tenantReducers";

import {
  tenantRegisterRegisterReducer,
  tenantRegisterVerifyEmailByCodeReducer,
  tenantRegisterResendVerificationEmailReducer,
  tenantRegisterRegisterGoogleReducer,
  tenantRegisterVerifyEmailByTokenReducer,
} from "./reducers/tenantRegisterReducers";

import {
  appointmentListReducer,
  appointmentCreateReducer,
  appointmentDeleteReducer,
  appointmentDetailsReducer,
  appointmentUpdateReducer,
} from "./reducers/appointmentReducers";
import {
  productListReducer,
  productDeleteReducer,
  productCreateReducer,
  productUpdateReducer,
  productDetailsReducer,
} from "./reducers/productReducers";
import { cartReducer } from "./reducers/cartReducers";
import {
  userRegisterReducer,
  userCreateReducer,
  userDetailsReducer,
  userEmployeeDetailsReducer,
  userUpdateProfileReducer,
  userDeleteReducer,
  userUpdateReducer,
  userListReducer,
  userUpdateStatusReducer,
  userPatientsListReducer,
  userEmployeesListReducer,
  userAddNoteReducer,
  userListNotesReducer,
  userCancelAllTreatmentsReducer,
  patientStatisticsReducer,
  userCreateEmployeeReducer,
  userDoctorServicesReducer,
  userAddressStatesReducer,
  userSendTreatmentPlanListEmailReducer,
  userPatientsSimpleListReducer,
  userAcceptTermsReducer,
  userAllSimpleListReducer,
  userEmployeeCoordinatorPlansReducer,
  userChangePasswordReducer,
} from "./reducers/userReducers";

import {
  getPatientsReducer,
  getAllPatientsReducer,
  createPatientReducer,
  deletePatientReducer,
  getPatientDetailsReducer,
  updatePatientReducer,
  getPatientActivityLogReducer,
  getPatientTreatmentPlansReducer,
  patientAddressStatesReducer,
  getPatientPortalPublicDetailsReducer,
  patientPortalVerifyPinReducer,
  patientRefreshPortalTokenReducer,
  getPatientPortalTreatmentPlansReducer,
  patientDownloadPlanPdfReducer,
  patientApprovePlanReducer,
  patientCancelPlanReducer,
} from "./reducers/patientReducers";

import {
  orderCreateReducer,
  orderListReducer,
  orderUpdateReducer,
  orderDeleteReducer,
} from "./reducers/orderReducers";

import {
  supplierListReducer,
  supplierCreateReducer,
  supplierDeleteReducer,
  supplierUpdateReducer,
} from "./reducers/supplierReducers";

import {
  categoryListReducer,
  categoryListAdminReducer,
  categoryDetailsReducer,
  categoryCreateReducer,
  categoryUpdateReducer,
  categoryDeleteReducer,
} from "./reducers/categoryReducers";

import {
  employeeListReducer,
  employeeListSimpleReducer,
  employeeCreateReducer,
  employeeDeleteReducer,
  employeeDetailsReducer,
  employeeUpdateReducer,
  employeeUpdateStatusReducer,
  employeeWorkScheduleReducer,
  employeeWorkScheduleUpdateReducer,
  employeeDocumentUploadReducer,
  employeeDocumentDeleteReducer,
} from "./reducers/employeeReducers";

import {
  serviceListReducer,
  serviceDetailsReducer,
  serviceCreateReducer,
  serviceUpdateReducer,
  serviceDeleteReducer,
  serviceUpdateStatusReducer,
} from "./reducers/serviceReducers";

import {
  serviceGroupListReducer,
  serviceGroupDetailsReducer,
  serviceGroupCreateReducer,
  serviceGroupUpdateReducer,
  serviceGroupDeleteReducer,
  serviceGroupUpdateStatusReducer,
  serviceGroupUpdateIsConsultingReducer,
} from "./reducers/serviceGroupReducers";

import {
  productCategoryListReducer,
  productCategoryDetailsReducer,
  productCategoryCreateReducer,
  productCategoryUpdateReducer,
  productCategoryDeleteReducer,
} from "./reducers/productCategoryReducers";

import {
  professionListReducer,
  professionDetailsReducer,
  professionCreateReducer,
  professionUpdateReducer,
  professionDeleteReducer,
  professionUpdateStatusReducer,
} from "./reducers/professionReducers";

import {
  professionGroupListReducer,
  professionGroupDetailsReducer,
  professionGroupCreateReducer,
  professionGroupUpdateReducer,
  professionGroupDeleteReducer,
  professionGroupUpdateStatusReducer,
  professionGroupUpdateIsConsultingReducer,
} from "./reducers/professionGroupReducers";

import {
  treatmentPlanListReducer,
  treatmentPlanDetailsReducer,
  treatmentPlanCreateReducer,
  treatmentPlanUpdateReducer,
  treatmentPlanDeleteReducer,
  treatmentPlanUpdateServicesOrderReducer,
  treatmentPlanUpdateStatusReducer,
  treatmentPlanApproveReducer,
  treatmentPlanCancelReducer,
  treatmentPlanStatisticsReducer,
  treatmentPlanStatisticsPerEmployeeReducer,
  treatmentPlanSendEmailReducer,
  treatmentPlanUpdateAvailablePublicReducer,
  treatmentPlanDownloadPdfReducer,
  treatmentPlanGetGlobalCountReducer,
  treatmentPlanDuplicateReducer,
} from "./reducers/treatmentPlanReducers";

import {
  treatmentStepListReducer,
  treatmentStepDetailsReducer,
  treatmentStepCreateReducer,
  treatmentStepUpdateReducer,
  treatmentStepDeleteReducer,
} from "./reducers/treatmentStepReducers";

import {
  statisticPatientReducer,
  statisticTreatmentPlanReducer,
  statisticTopServiceReducer,
  statisticTopServiceGroupReducer,
  statisticTopEmployeeReducer,
  statisticCoordinatorReducer,
  statisticTopServiceDoctorReducer,
  statisticTreatmentPlanEmployeeReducer,
  statisticCoordinatorUserReducer,
  statisticEmployeeDoctorReducer,
  statisticMonthlyTreatmentPlanReducer,
  statisticCaseAcceptanceReducer,
  statisticMonthlyTreatmentPlanStackedReducer,
  statisticPatientNewReducer,
} from "./reducers/statisticReducers";

import {
  speakerListReducer,
  speakerDetailsReducer,
  speakerCreateReducer,
  speakerUpdateReducer,
  speakerDeleteReducer,
  speakerAddCourseReducer,
  speakerRemoveCourseReducer,
  speakerStatusUpdateReducer,
  speakerDescriptionUpdateReducer,
  speakerCoursesListReducer,
  speakerImageUpdateReducer,
} from "./reducers/speakerReducers";

import {
  attendeeListReducer,
  attendeeDetailsReducer,
  attendeeCreateReducer,
  attendeeUpdateReducer,
  attendeeDeleteReducer,
  attendeeAddCourseReducer,
  attendeeRemoveCourseReducer,
  attendeeStatusUpdateReducer,
  attendeeCoursesListReducer,
  attendeeCourseStatusUpdateReducer,
  attendeeCoursePaidStatusUpdateReducer,
  attendeeUpdateImageReducer,
} from "./reducers/attendeeReducers";

import {
  courseListReducer,
  courseDetailsReducer,
  courseUpdateReducer,
  courseCreateReducer,
  courseDeleteReducer,
  courseAttendeesListReducer,
  courseAttendeeAddReducer,
  courseAttendeeRemoveReducer,
  courseSpeakersListReducer,
  courseSpeakerAddReducer,
  courseSpeakerRemoveReducer,
  courseStatusUpdateReducer,
  courseAttendeeStatusUpdateReducer,
  courseDescriptionUpdateReducer,
  courseTasksListReducer,
  courseAttendeePaidStatusUpdateReducer,
  courseTaskAddReducer,
  courseTaskUpdateReducer,
  courseTasksPositionsUpdateReducer,
  courseUpdatePricingReducer,
  courseUpdateImageReducer,
  courseTaskRemoveReducer,
  courseTaskSubtaskAddReducer,
  courseTaskSubtaskRemoveReducer,
  courseAttendeePriceUpdateReducer,
} from "./reducers/courseReducers";

import {
  publicPatientTreatmentPlanListReducer,
  publicPatientTreatmentPlanDetailsReducer,
  publicTenantDetailsReducer,
  publicPatientApproveTreatmentPlanReducer,
  publicPatientCancelTreatmentPlanReducer,
  publicPatientDownloadPdfTreatmentPlanReducer,
  publicCoursesListReducer,
  publicAttendeeCourseRegisterReducer,
  publicInviteInfoReducer,
  publicAcceptInviteReducer,
  publicPortalPatientDetailsReducer,
  publicPortalPatientVerifyPinReducer,
  publicPortalGetTreatmentPlansReducer,
  publicPortalTreatmentPlanApproveReducer,
  publicPortalTreatmentPlanCancelReducer,
  publicPortalTreatmentPlanDownloadPdfReducer,
  publicCheckUserInviteReducer,
} from "./reducers/publicReducers";

import {
  clientListReducer,
  clientDetailsReducer,
  clientCreateReducer,
  clientUpdateReducer,
  clientDeleteReducer,
} from "./reducers/clientReducers";

import {
  paymentTenantSetupIntentReducer,
  paymentTenantCreateSubscriptionAutoTaxReducer,
  paymentTenantGetStripeBillingDetailsReducer,
  paymentTenantCheckUpdateVatCodeReducer,
  paymentTenantUpdateAddressCreateSubscriptionReducer,
  paymentTenantRemoveAllTaxIdsReducer,
  paymentTenantGetStripePaymentMethodsReducer,
  paymentUpdateSubscriptionBillingAndCreateCheckoutReducer,
  paymentTenantCancelSubscriptionReducer,
  paymentTenantGetModulesReducer,
} from "./reducers/paymentReducers";

const reducer = combineReducers({
  authLogin: authLoginReducer,
  authGoogleLogin: authGoogleLoginReducer,
  authResetPassword: authResetPasswordReducer,
  authRequestResetPassword: authRequestResetPasswordReducer,
  authSendResetEmailAccount: authSendResetEmailAccountReducer,
  authVerifyResetPasswordToken: authVerifyResetPasswordTokenReducer,
  authUpdatePassword: authUpdatePasswordReducer,
  authVerifyLoginCode: authVerifyLoginCodeReducer,
  authSelectAccountLogin: authSelectAccountLoginReducer,
  authSwitchTenant: authSwitchTenantReducer,

  tenantDetails: tenantDetailsReducer,
  tenantUpdateClinicName: tenantUpdateClinicNameReducer,
  tenantUpdateSupportEmail: tenantUpdateSupportEmailReducer,
  tenantUpdateOperatingCountry: tenantUpdateOperatingCountryReducer,
  tenantUpdateOnboardingStatus: tenantUpdateOnboardingStatusReducer,
  tenantSeedDatabaseUpdateOnboardingStatus:
    tenantSeedDatabaseUpdateOnboardingStatusReducer,
  tenantUpdateLogo: tenantUpdateLogoReducer,
  tenantUpdateClinicDetails: tenantUpdateClinicDetailsReducer,
  tenantUpdateRegionalSettings: tenantUpdateRegionalSettingsReducer,
  tenantUpdateSchedulingSettings: tenantUpdateSchedulingSettingsReducer,
  tenantUpdateDentalChairs: tenantUpdateDentalChairsReducer,
  tenantUpdateCompanyDetails: tenantUpdateCompanyDetailsReducer,
  tenantUpdateSubscriptionStatus: tenantUpdateSubscriptionStatusReducer,
  tenantGetSubscriptionUsage: tenantGetSubscriptionUsageReducer,
  tenantGetStripeInvoices: tenantGetStripeInvoicesReducer,
  tenantGetPublicDetails: tenantGetPublicDetailsReducer,
  tenantDetailsBySlug: tenantDetailsBySlugReducer,
  tenantInviteUserToTenant: tenantInviteUserToTenantReducer,
  tenantUpdateDefaultCoordinator: tenantUpdateDefaultCoordinatorReducer,
  tenantUpdateTenantUserPermissions: tenantUpdateTenantUserPermissionsReducer,
  tenantDeleteAccount: tenantDeleteAccountReducer,

  paymentTenantSetupIntent: paymentTenantSetupIntentReducer,
  paymentTenantCreateSubscriptionAutoTax:
    paymentTenantCreateSubscriptionAutoTaxReducer,
  paymentTenantGetStripeBillingDetails:
    paymentTenantGetStripeBillingDetailsReducer,
  paymentTenantCheckUpdateVatCode: paymentTenantCheckUpdateVatCodeReducer,
  paymentTenantUpdateAddressCreateSubscription:
    paymentTenantUpdateAddressCreateSubscriptionReducer,
  paymentTenantRemoveAllTaxIds: paymentTenantRemoveAllTaxIdsReducer,
  paymentTenantGetStripePaymentMethods:
    paymentTenantGetStripePaymentMethodsReducer,
  paymentUpdateSubscriptionBillingAndCreateCheckout:
    paymentUpdateSubscriptionBillingAndCreateCheckoutReducer,
  paymentTenantCancelSubscription: paymentTenantCancelSubscriptionReducer,
  paymentTenantGetModules: paymentTenantGetModulesReducer,

  // tenantRegister: tenantRegisterReducer,
  // tenantVerify: tenantVerifyReducer,
  // tenantDetails: tenantDetailsReducer,
  // tenantUpdate: tenantUpdateReducer,
  // tenantUpdateAdminUser: tenantUpdateAdminUserReducer,
  // tenantUpdateType: tenantUpdateTypeReducer,
  // tenantUpdateOperatingRooms: tenantUpdateOperatingRoomsReducer,
  // tenantUpdateImage: tenantUpdateImageReducer,
  // tenantPaymentIntent: tenantPaymentIntentReducer,
  // tenantSubscriptionCreate: tenantSubscriptionCreateReducer,
  // tenantModuleToggleActivation: tenantModuleToggleActivationReducer,
  // tenantSetupIntent: tenantSetupIntentReducer,
  // tenantStripeSubscriptionDetails: tenantStripeSubscriptionDetailsReducer,
  // tenantCreateSubscriptionNoPaymentMethod:
  //   tenantCreateSubscriptionNoPaymentMethodReducer,
  // tenantUpdateSetupStatus: tenantUpdateSetupStatusReducer,
  // tenantGetSetupInformation: tenantGetSetupInformationReducer,
  // tenantUpdateModules: tenantUpdateModulesReducer,
  // tenantOnboardingInformation: tenantOnboardingInformationReducer,
  // tenantUpdatePhone: tenantUpdatePhoneReducer,
  // tenantUpdateServiceEmail: tenantUpdateServiceEmailReducer,
  // tenantUpdateAddress: tenantUpdateAddressReducer,
  // tenantGetStripeSubscriptionDetailsNew:
  //   tenantGetStripeSubscriptionDetailsNewReducer,
  // tenantGetStripePaymentMethods: tenantGetStripePaymentMethodsReducer,
  // tenantCreateStripePaymentMethod: tenantCreateStripePaymentMethodReducer,
  // tenantGetStripeBillingDetails: tenantGetStripeBillingDetailsReducer,
  // tenantUpdateStripeBillingDetails: tenantUpdateStripeBillingDetailsReducer,
  // tenantUpdateAddressCreateSubscription:
  //   tenantUpdateAddressCreateSubscriptionReducer,
  // tenantCreateSubscriptionAutoTax: tenantCreateSubscriptionAutoTaxReducer,
  // tenantCheckUpdateVatCode: tenantCheckUpdateVatCodeReducer,
  // tenantUpdateAddressUpdateSubscription:
  //   tenantUpdateAddressUpdateSubscriptionReducer,
  // tenantRemoveAllTaxIds: tenantRemoveAllTaxIdsReducer,
  // tenantResendVerificationEmail: tenantResendVerificationEmailReducer,
  // tenantGetStripeInvoices: tenantGetStripeInvoicesReducer,
  // tenantCancelCurrentSubscription: tenantCancelCurrentSubscriptionReducer,
  // superAdminTenantList: superAdminTenantListReducer,

  tenantRegisterRegister: tenantRegisterRegisterReducer,
  tenantRegisterVerifyEmailByCode: tenantRegisterVerifyEmailByCodeReducer,
  tenantRegisterRegisterGoogle: tenantRegisterRegisterGoogleReducer,
  tenantRegisterResendVerificationEmail:
    tenantRegisterResendVerificationEmailReducer,
  tenantRegisterVerifyEmailByToken: tenantRegisterVerifyEmailByTokenReducer,

  treatmentStepList: treatmentStepListReducer,
  treatmentStepDetails: treatmentStepDetailsReducer,
  treatmentStepCreate: treatmentStepCreateReducer,
  treatmentStepUpdate: treatmentStepUpdateReducer,
  treatmentStepDelete: treatmentStepDeleteReducer,

  treatmentPlanList: treatmentPlanListReducer,
  treatmentPlanDetails: treatmentPlanDetailsReducer,
  treatmentPlanCreate: treatmentPlanCreateReducer,
  treatmentPlanUpdate: treatmentPlanUpdateReducer,
  treatmentPlanDelete: treatmentPlanDeleteReducer,
  treatmentPlanUpdateServicesOrder: treatmentPlanUpdateServicesOrderReducer,
  treatmentPlanUpdateStatus: treatmentPlanUpdateStatusReducer,
  treatmentPlanApprove: treatmentPlanApproveReducer,
  treatmentPlanCancel: treatmentPlanCancelReducer,
  treatmentPlanStatistics: treatmentPlanStatisticsReducer,
  treatmentPlanStatisticsPerEmployee: treatmentPlanStatisticsPerEmployeeReducer,
  treatmentPlanSendEmail: treatmentPlanSendEmailReducer,
  treatmentPlanUpdateAvailablePublic: treatmentPlanUpdateAvailablePublicReducer,
  treatmentPlanDownloadPdf: treatmentPlanDownloadPdfReducer,
  treatmentPlanGetGlobalCount: treatmentPlanGetGlobalCountReducer,
  treatmentPlanDuplicate: treatmentPlanDuplicateReducer,

  serviceList: serviceListReducer,
  serviceDetails: serviceDetailsReducer,
  serviceCreate: serviceCreateReducer,
  serviceUpdate: serviceUpdateReducer,
  serviceDelete: serviceDeleteReducer,
  serviceUpdateStatus: serviceUpdateStatusReducer,

  serviceGroupList: serviceGroupListReducer,
  serviceGroupDetails: serviceGroupDetailsReducer,
  serviceGroupCreate: serviceGroupCreateReducer,
  serviceGroupUpdate: serviceGroupUpdateReducer,
  serviceGroupDelete: serviceGroupDeleteReducer,
  serviceGroupUpdateStatus: serviceGroupUpdateStatusReducer,
  serviceGroupUpdateIsConsulting: serviceGroupUpdateIsConsultingReducer,

  productCategoryList: productCategoryListReducer,
  productCategoryDetails: productCategoryDetailsReducer,
  productCategoryCreate: productCategoryCreateReducer,
  productCategoryUpdate: productCategoryUpdateReducer,
  productCategoryDelete: productCategoryDeleteReducer,

  getPatients: getPatientsReducer,
  getAllPatients: getAllPatientsReducer,
  createPatient: createPatientReducer,
  deletePatient: deletePatientReducer,
  getPatientDetails: getPatientDetailsReducer,
  updatePatient: updatePatientReducer,
  patientActivityLog: getPatientActivityLogReducer,
  getPatientTreatmentPlans: getPatientTreatmentPlansReducer,
  patientAddressStates: patientAddressStatesReducer,
  getPatientPortalPublicDetails: getPatientPortalPublicDetailsReducer,
  patientPortalVerifyPin: patientPortalVerifyPinReducer,
  patientRefreshPortalToken: patientRefreshPortalTokenReducer,
  getPatientPortalTreatmentPlans: getPatientPortalTreatmentPlansReducer,
  patientDownloadPlanPdf: patientDownloadPlanPdfReducer,
  patientApprovePlan: patientApprovePlanReducer,
  patientCancelPlan: patientCancelPlanReducer,

  professionList: professionListReducer,
  professionDetails: professionDetailsReducer,
  professionCreate: professionCreateReducer,
  professionUpdate: professionUpdateReducer,
  professionDelete: professionDeleteReducer,
  professionUpdateStatus: professionUpdateStatusReducer,

  professionGroupList: professionGroupListReducer,
  professionGroupDetails: professionGroupDetailsReducer,
  professionGroupCreate: professionGroupCreateReducer,
  professionGroupUpdate: professionGroupUpdateReducer,
  professionGroupDelete: professionGroupDeleteReducer,
  professionGroupUpdateStatus: professionGroupUpdateStatusReducer,
  professionGroupUpdateIsConsulting: professionGroupUpdateIsConsultingReducer,

  employeeList: employeeListReducer,
  employeeListSimple: employeeListSimpleReducer,
  employeeCreate: employeeCreateReducer,
  employeeDelete: employeeDeleteReducer,
  employeeDetails: employeeDetailsReducer,
  employeeUpdate: employeeUpdateReducer,
  employeeUpdateStatus: employeeUpdateStatusReducer,
  employeeWorkSchedule: employeeWorkScheduleReducer,
  employeeWorkScheduleUpdate: employeeWorkScheduleUpdateReducer,
  employeeDocumentUpload: employeeDocumentUploadReducer,
  employeeDocumentDelete: employeeDocumentDeleteReducer,

  appointmentList: appointmentListReducer,
  appointmentDetails: appointmentDetailsReducer,
  appointmentCreate: appointmentCreateReducer,
  appointmentUpdate: appointmentUpdateReducer,
  appointmentDelete: appointmentDeleteReducer,

  productList: productListReducer,
  productDelete: productDeleteReducer,
  productCreate: productCreateReducer,
  productUpdate: productUpdateReducer,
  productDetails: productDetailsReducer,

  cart: cartReducer,

  userRegister: userRegisterReducer,
  userCreate: userCreateReducer,
  userDetails: userDetailsReducer,
  userEmployeeDetails: userEmployeeDetailsReducer,
  userUpdateProfile: userUpdateProfileReducer,
  userList: userListReducer,
  userDelete: userDeleteReducer,
  userUpdate: userUpdateReducer,
  userUpdateStatus: userUpdateStatusReducer,
  userPatientsList: userPatientsListReducer,
  userEmployeesList: userEmployeesListReducer,
  userAddNote: userAddNoteReducer,
  userListNotes: userListNotesReducer,
  userCancelAllTreatments: userCancelAllTreatmentsReducer,
  userCreateEmployee: userCreateEmployeeReducer,
  userDoctorServices: userDoctorServicesReducer,
  userAddressStates: userAddressStatesReducer,
  userSendTreatmentPlanListEmail: userSendTreatmentPlanListEmailReducer,
  userPatientsSimpleList: userPatientsSimpleListReducer,
  userAcceptTerms: userAcceptTermsReducer,
  userAllSimpleList: userAllSimpleListReducer,
  userEmployeeCoordinatorPlans: userEmployeeCoordinatorPlansReducer,
  userChangePassword: userChangePasswordReducer,

  patientStatistics: patientStatisticsReducer,

  orderCreate: orderCreateReducer,
  orderList: orderListReducer,
  orderUpdate: orderUpdateReducer,
  orderDelete: orderDeleteReducer,

  supplierList: supplierListReducer,
  supplierCreate: supplierCreateReducer,
  supplierDelete: supplierDeleteReducer,
  supplierUpdate: supplierUpdateReducer,

  categoryList: categoryListReducer,
  categoryListAdmin: categoryListAdminReducer,
  categoryDetails: categoryDetailsReducer,
  categoryCreate: categoryCreateReducer,
  categoryUpdate: categoryUpdateReducer,
  categoryDelete: categoryDeleteReducer,

  statisticPatient: statisticPatientReducer,
  statisticTreatmentPlan: statisticTreatmentPlanReducer,
  statisticTopService: statisticTopServiceReducer,
  statisticTopServiceGroup: statisticTopServiceGroupReducer,
  statisticTopEmployee: statisticTopEmployeeReducer,
  statisticCoordinator: statisticCoordinatorReducer,
  statisticTopServiceDoctor: statisticTopServiceDoctorReducer,
  statisticTreatmentPlanEmployee: statisticTreatmentPlanEmployeeReducer,
  statisticCoordinatorUser: statisticCoordinatorUserReducer,
  statisticEmployeeDoctor: statisticEmployeeDoctorReducer,
  statisticMonthlyTreatmentPlan: statisticMonthlyTreatmentPlanReducer,
  statisticCaseAcceptance: statisticCaseAcceptanceReducer,
  statisticMonthlyTreatmentPlanStacked:
    statisticMonthlyTreatmentPlanStackedReducer,
  statisticPatientNew: statisticPatientNewReducer,

  speakerList: speakerListReducer,
  speakerDetails: speakerDetailsReducer,
  speakerCreate: speakerCreateReducer,
  speakerUpdate: speakerUpdateReducer,
  speakerDelete: speakerDeleteReducer,
  speakerAddCourse: speakerAddCourseReducer,
  speakerRemoveCourse: speakerRemoveCourseReducer,
  speakerStatusUpdate: speakerStatusUpdateReducer,
  speakerDescriptionUpdate: speakerDescriptionUpdateReducer,
  speakerCoursesList: speakerCoursesListReducer,
  speakerUpdateImage: speakerImageUpdateReducer,

  attendeeList: attendeeListReducer,
  attendeeDetails: attendeeDetailsReducer,
  attendeeCreate: attendeeCreateReducer,
  attendeeUpdate: attendeeUpdateReducer,
  attendeeDelete: attendeeDeleteReducer,
  attendeeAddCourse: attendeeAddCourseReducer,
  attendeeRemoveCourse: attendeeRemoveCourseReducer,
  attendeeStatusUpdate: attendeeStatusUpdateReducer,
  attendeeCoursesList: attendeeCoursesListReducer,
  attendeeCourseStatusUpdate: attendeeCourseStatusUpdateReducer,
  attendeeCoursePaidStatusUpdate: attendeeCoursePaidStatusUpdateReducer,
  attendeeUpdateImage: attendeeUpdateImageReducer,

  courseList: courseListReducer,
  courseDetails: courseDetailsReducer,
  courseUpdate: courseUpdateReducer,
  courseCreate: courseCreateReducer,
  courseDelete: courseDeleteReducer,
  courseAttendeesList: courseAttendeesListReducer,
  courseAttendeeAdd: courseAttendeeAddReducer,
  courseAttendeeRemove: courseAttendeeRemoveReducer,
  courseSpeakersList: courseSpeakersListReducer,
  courseSpeakerAdd: courseSpeakerAddReducer,
  courseSpeakerRemove: courseSpeakerRemoveReducer,
  courseStatusUpdate: courseStatusUpdateReducer,
  courseAttendeeStatusUpdate: courseAttendeeStatusUpdateReducer,
  courseDescriptionUpdate: courseDescriptionUpdateReducer,
  courseTasksList: courseTasksListReducer,
  courseAttendeePaidStatusUpdate: courseAttendeePaidStatusUpdateReducer,
  courseTaskAdd: courseTaskAddReducer,
  courseTaskUpdate: courseTaskUpdateReducer,
  courseTasksPositionsUpdate: courseTasksPositionsUpdateReducer,
  courseUpdatePricing: courseUpdatePricingReducer,
  courseUpdateImage: courseUpdateImageReducer,
  courseTaskRemove: courseTaskRemoveReducer,
  courseTaskSubtaskAdd: courseTaskSubtaskAddReducer,
  courseTaskSubtaskRemove: courseTaskSubtaskRemoveReducer,
  courseAttendeePriceUpdate: courseAttendeePriceUpdateReducer,

  publicPatientTreatmentPlanList: publicPatientTreatmentPlanListReducer,
  publicPatientTreatmentPlanDetails: publicPatientTreatmentPlanDetailsReducer,
  publicTenantDetails: publicTenantDetailsReducer,
  publicPatientApproveTreatmentPlan: publicPatientApproveTreatmentPlanReducer,
  publicPatientCancelTreatmentPlan: publicPatientCancelTreatmentPlanReducer,
  publicPatientDownloadPdfTreatmentPlan:
    publicPatientDownloadPdfTreatmentPlanReducer,
  publicCoursesList: publicCoursesListReducer,
  publicAttendeeCourseRegister: publicAttendeeCourseRegisterReducer,

  publicInviteInfo: publicInviteInfoReducer,
  publicAcceptInvite: publicAcceptInviteReducer,
  publicPortalPatientDetails: publicPortalPatientDetailsReducer,
  publicPortalPatientVerifyPin: publicPortalPatientVerifyPinReducer,
  publicPortalGetTreatmentPlans: publicPortalGetTreatmentPlansReducer,
  publicPortalTreatmentPlanApprove: publicPortalTreatmentPlanApproveReducer,
  publicPortalTreatmentPlanCancel: publicPortalTreatmentPlanCancelReducer,
  publicPortalTreatmentPlanDownloadPdf:
    publicPortalTreatmentPlanDownloadPdfReducer,
  publicCheckUserInvite: publicCheckUserInviteReducer,

  clientList: clientListReducer,
  clientDetails: clientDetailsReducer,
  clientCreate: clientCreateReducer,
  clientUpdate: clientUpdateReducer,
  clientDelete: clientDeleteReducer,
});

const cartItemsFromStorage = localStorage.getItem("cartItems")
  ? JSON.parse(localStorage.getItem("cartItems"))
  : [];

const userInfoFromStorage = localStorage.getItem("userInfo")
  ? JSON.parse(localStorage.getItem("userInfo"))
  : null;

const patientInfoFromStorage = localStorage.getItem("patientInfo")
  ? JSON.parse(localStorage.getItem("patientInfo"))
  : null;

const shippingAddressFromStorage = localStorage.getItem("shippingAddress")
  ? JSON.parse(localStorage.getItem("shippingAddress"))
  : {};

const initialState = {
  cart: {
    cartItems: cartItemsFromStorage,
    shippingAddress: shippingAddressFromStorage,
  },
  authLogin: { userInfo: userInfoFromStorage },
  patientPortalVerifyPin: {
    patientToken: patientInfoFromStorage,
  },
};

const middleware = [thunk];

const store = createStore(
  reducer,
  initialState,
  composeWithDevTools(applyMiddleware(...middleware))
);

export default store;
